/**
 * Scoringsmotor til Barefoot sadelguide — 1:1 port af scoring.py (LOGIK.md §3–§7).
 * Exposes window.SadelguideScoring
 */
(function (global) {
  'use strict';

  const SAEDE_VAEGT = 4;
  const KNAESTOETTER_VAEGT = 3;
  const STOERRELSE_VAEGT = 3;
  const DISCIPLIN_VAEGT = 1;
  const PRO_VAEGT = 2;
  const HESTEHENSYN_VAEGT = 1;
  const RIDESKOLE_BONUS_VAEGT = 1;
  const WELLINGTON_STOETTE_BONUS_VAEGT = 1;
  const LEXINGTON_HOEJDE_GRAENSE_CM = 172;

  const VARIANT_GRUPPER = [
    new Set(['Cheyenne', 'Cheyenne (læder)', 'VegiTex Cheyenne']),
    new Set(['Cherokee', 'Cherokee Classic']),
    new Set(['Pro Cherokee', 'Pro Cherokee Classic']),
    new Set(['Arizona Nut', 'Arizona Rose']),
    new Set(['Missoula Nut', 'Virginia Rose']),
  ];

  const RYGFORM_BONUS_MODELLER = {
    kort_ryg: new Set(['Arizona', 'Pro Arizona', 'Arizona Nut', 'Arizona Rose']),
    bred_lille_manke: new Set(['Nevada', 'Pro Barrydale']),
    hoejt_mankeben: new Set(['Lexington (kort)', 'Lexington (lang)', 'Pro Lexington']),
    lang_ryg: new Set(['Lexington (kort)', 'Lexington (lang)', 'Pro Lexington']),
    skraa_ryg_bagud: new Set(['Lexington (kort)', 'Lexington (lang)', 'Pro Lexington']),
    spinkel_bygning: new Set(['Arizona Nut', 'Arizona Rose']),
  };

  const HESTETYPE_BONUS_MODELLER = {
    Merlyn: new Set(['cob', 'irish_cob', 'tinker', 'draft_hest']),
    'VegiTex Merlyn': new Set(['cob', 'irish_cob', 'tinker', 'draft_hest']),
    'Lexington (kort)': new Set(['islaender', 'tinker', 'pony_lille_hest']),
    Atlanta: new Set(['islaender', 'tinker', 'pony_lille_hest']),
  };

  function parseBuksestrBaand(tekst) {
    if (!tekst) return null;
    const renset = String(tekst).replace(/\([^)]*\)/g, '');
    const baand = [];
    for (const raw of renset.split('|')) {
      const token = raw.trim();
      const m = token.match(/^(\d+):(.+)$/);
      if (!m) return null;
      const stoerrelse = parseInt(m[1], 10);
      const tal = (m[2].match(/\d+/g) || []).map((x) => parseInt(x, 10));
      if (!tal.length) return null;
      baand.push([stoerrelse, Math.min(...tal), Math.max(...tal)]);
    }
    return baand;
  }

  function parseMinPris(prisfelt) {
    if (!prisfelt) return null;
    const tal = String(prisfelt).match(/\d+/g);
    if (!tal || !tal.length) return null;
    return Math.min(...tal.map((x) => parseInt(x, 10)));
  }

  function bestemMaalstoerrelse(sadel, svar) {
    const haardRegelAktiv =
      svar.koen === 'kvinde' &&
      svar.hoejde_cm != null &&
      svar.hoejde_cm > 173 &&
      svar.knaestoetter_oenske === 'ja';
    if (haardRegelAktiv) return 2;

    if (svar.bukse_stoerrelse == null) return null;

    const felt = svar.koen === 'kvinde' ? 'buksestr_dame' : 'buksestr_herre';
    const baand = parseBuksestrBaand(sadel[felt]);
    if (baand == null) return null;

    const matches = baand.filter(([, lo, hi]) => lo <= svar.bukse_stoerrelse && svar.bukse_stoerrelse <= hi);
    return matches.length ? matches[0][0] : null;
  }

  function scoreSaede(sadel, svar) {
    const kategori = sadel.saede.kategori;
    if (kategori === 'justerbar') return SAEDE_VAEGT;
    return kategori === svar.saede_oenske ? SAEDE_VAEGT : 0;
  }

  function scoreKnaestoetter(sadel, svar) {
    if (svar.knaestoetter_oenske === 'ligegyldigt') return 0;
    const oenskerKnae = svar.knaestoetter_oenske === 'ja';
    const harKnae = sadel.knaestoetter.har_knaestoetter;
    return oenskerKnae === harKnae ? KNAESTOETTER_VAEGT : 0;
  }

  function scoreStoerrelse(sadel, svar) {
    const maalstoerrelse = bestemMaalstoerrelse(sadel, svar);
    if (maalstoerrelse == null) return [0, null];
    if (sadel.stoerrelser.liste.includes(maalstoerrelse)) return [STOERRELSE_VAEGT, maalstoerrelse];
    return [0, maalstoerrelse];
  }

  function disciplinMatcher(sadel, svar) {
    const disciplin = svar.disciplin === 'rideskole' ? 'allround' : svar.disciplin;
    return sadel.discipliner.liste.includes(disciplin);
  }

  function scoreDisciplin(sadel, svar) {
    return disciplinMatcher(sadel, svar) ? DISCIPLIN_VAEGT : 0;
  }

  function scoreRideskoleBonus(sadel, svar) {
    if (svar.disciplin === 'rideskole' && sadel.discipliner.rideskole_bonus) return RIDESKOLE_BONUS_VAEGT;
    return 0;
  }

  function scorePro(sadel) {
    return sadel.pro_model.er_pro ? PRO_VAEGT : 0;
  }

  function scoreHestehensyn(sadel, svar) {
    const model = sadel.model;
    let bonus = 0;
    if (svar.hest_rygform && (RYGFORM_BONUS_MODELLER[svar.hest_rygform] || new Set()).has(model)) {
      bonus += HESTEHENSYN_VAEGT;
    }
    if (svar.hest_type && (HESTETYPE_BONUS_MODELLER[model] || new Set()).has(svar.hest_type)) {
      bonus += HESTEHENSYN_VAEGT;
    }
    return bonus;
  }

  function scoreWellingtonStoetteBonus(sadel, svar) {
    if (sadel.model !== 'Wellington') return 0;
    const dressurValgt = svar.disciplin === 'dressur';
    const oenskerMegetStoette = svar.saede_oenske === 'dybt' && svar.knaestoetter_oenske === 'ja';
    return dressurValgt || oenskerMegetStoette ? WELLINGTON_STOETTE_BONUS_VAEGT : 0;
  }

  function scoreLexingtonHoejde(sadel, svar) {
    const model = sadel.model;
    if ((model !== 'Lexington (kort)' && model !== 'Lexington (lang)') || svar.hoejde_cm == null) return 0;
    if (model === 'Lexington (kort)' && svar.hoejde_cm < LEXINGTON_HOEJDE_GRAENSE_CM) return HESTEHENSYN_VAEGT;
    if (model === 'Lexington (lang)' && svar.hoejde_cm >= LEXINGTON_HOEJDE_GRAENSE_CM) return HESTEHENSYN_VAEGT;
    return 0;
  }

  function scoreSadel(sadel, svar) {
    const saedeP = scoreSaede(sadel, svar);
    const knaeP = scoreKnaestoetter(sadel, svar);
    const [stoerrelseP, maalstoerrelse] = scoreStoerrelse(sadel, svar);
    const disciplinP = scoreDisciplin(sadel, svar);
    const proP = scorePro(sadel);
    const rideskoleP = scoreRideskoleBonus(sadel, svar);
    const hesterygP = scoreHestehensyn(sadel, svar) + scoreLexingtonHoejde(sadel, svar);
    const wellingtonP = scoreWellingtonStoetteBonus(sadel, svar);

    const kerneScore = saedeP + knaeP + stoerrelseP + disciplinP + proP;
    const total = kerneScore + rideskoleP + hesterygP + wellingtonP;

    return {
      model: sadel.model,
      total_score: total,
      kerne_score: kerneScore,
      detaljer: {
        saede: saedeP,
        knaestoetter: knaeP,
        stoerrelse: stoerrelseP,
        disciplin: disciplinP,
        pro_model: proP,
        rideskole_bonus: rideskoleP,
        hestehensyn: hesterygP,
        wellington_stoette_bonus: wellingtonP,
      },
      maalstoerrelse,
      er_vegansk: sadel.vegansk.er_vegansk,
      betinget_vegansk: sadel.vegansk.betinget_vegansk,
    };
  }

  function variantgruppeFor(model) {
    for (const gruppe of VARIANT_GRUPPER) {
      if (gruppe.has(model)) return gruppe;
    }
    return null;
  }

  function sortScoreKey(a, b) {
    if (b.total_score !== a.total_score) return b.total_score - a.total_score;
    if (b.kerne_score !== a.kerne_score) return b.kerne_score - a.kerne_score;
    return a.model.localeCompare(b.model, 'da');
  }

  function kollapsVariantgrupper(scoreListe, sadelPrModel, svar) {
    const listePrModel = Object.fromEntries(scoreListe.map((r) => [r.model, r]));
    const behandlet = new Set();
    const resultat = [];

    for (const r of scoreListe) {
      const model = r.model;
      if (behandlet.has(model)) continue;
      const gruppe = variantgruppeFor(model);
      if (gruppe == null) {
        resultat.push(r);
        behandlet.add(model);
        continue;
      }

      const medlemmer = [...gruppe].filter((m) => listePrModel[m]).map((m) => listePrModel[m]);
      for (const m of gruppe) behandlet.add(m);

      let vinder = r;
      if (svar.vegansk_vigtigt) {
        const veganskMedlemmer = medlemmer.filter((m) => m.er_vegansk);
        if (veganskMedlemmer.length && !vinder.er_vegansk) {
          vinder = veganskMedlemmer[0];
        }
      }

      const vinderMedVarianter = { ...vinder };
      vinderMedVarianter.varianter =
        medlemmer.length > 1
          ? medlemmer.map((m) => ({
              model: m.model,
              materiale: sadelPrModel[m.model].materiale,
              pris: parseMinPris(sadelPrModel[m.model].katalogpris_reference),
              er_vegansk: m.er_vegansk,
            }))
          : [];
      resultat.push(vinderMedVarianter);
    }

    return resultat.sort(sortScoreKey);
  }

  function findBedsteMatch(sadler, svar) {
    const sadelPrModel = Object.fromEntries(sadler.map((s) => [s.model, s]));
    let scoreSorteret = sadler.map((s) => scoreSadel(s, svar)).sort(sortScoreKey);
    scoreSorteret = kollapsVariantgrupper(scoreSorteret, sadelPrModel, svar);

    const disciplinMatchende = scoreSorteret.filter((r) => disciplinMatcher(sadelPrModel[r.model], svar));
    const kandidatPulje = disciplinMatchende.length ? disciplinMatchende : scoreSorteret;

    let anker;
    if (svar.vegansk_vigtigt) {
      const relevantVegansk = kandidatPulje.filter((r) => r.er_vegansk && r.total_score > 0);
      anker = relevantVegansk.length ? relevantVegansk[0] : kandidatPulje[0];
    } else {
      anker = kandidatPulje[0];
    }

    const bedsteGruppe = kandidatPulje.filter(
      (r) => r.total_score === anker.total_score && r.kerne_score === anker.kerne_score
    );
    const bedsteModeller = new Set(bedsteGruppe.map((r) => r.model));

    const resten = kandidatPulje.filter((r) => !bedsteModeller.has(r.model));
    const alternativ = resten.length ? resten[0] : null;

    const valgte = new Set(bedsteModeller);
    if (alternativ) valgte.add(alternativ.model);

    let naestbedsteIkkeVegansk = null;
    if (svar.vegansk_vigtigt && bedsteGruppe.every((r) => r.er_vegansk)) {
      naestbedsteIkkeVegansk = kandidatPulje.find((r) => !r.er_vegansk) || null;
    }

    function harMinimumskvalitet(r) {
      return r.detaljer.saede > 0 || r.detaljer.knaestoetter > 0;
    }

    let kvalificerede = resten.filter(
      (r) => r.total_score > 0 && !valgte.has(r.model) && harMinimumskvalitet(r)
    );
    if (!kvalificerede.length) {
      kvalificerede = scoreSorteret.filter(
        (r) => r.total_score > 0 && !valgte.has(r.model) && harMinimumskvalitet(r)
      );
    }
    kvalificerede.sort((a, b) => {
      const pa = parseMinPris(sadelPrModel[a.model].katalogpris_reference);
      const pb = parseMinPris(sadelPrModel[b.model].katalogpris_reference);
      const va = pa == null ? Infinity : pa;
      const vb = pb == null ? Infinity : pb;
      return va - vb;
    });
    const prisvenligt = kvalificerede.length ? kvalificerede[0] : null;

    return {
      bedste_match: bedsteGruppe,
      alternativ,
      prisvenligt_valg: prisvenligt,
      naestbedste_ikke_veganske_match: naestbedsteIkkeVegansk,
    };
  }

  /**
   * Rough clothing-size estimate from height when the customer only gives height.
   * Used so size scoring still works when clothing size is skipped.
   */
  function estimerBukseStoerrelse(koen, hoejdeCm) {
    if (hoejdeCm == null || Number.isNaN(hoejdeCm)) return null;
    if (koen === 'kvinde') {
      if (hoejdeCm < 158) return 34;
      if (hoejdeCm < 165) return 36;
      if (hoejdeCm < 172) return 38;
      if (hoejdeCm < 178) return 40;
      return 42;
    }
    if (hoejdeCm < 170) return 46;
    if (hoejdeCm < 178) return 48;
    if (hoejdeCm < 185) return 50;
    return 52;
  }

  global.SadelguideScoring = {
    findBedsteMatch,
    scoreSadel,
    parseMinPris,
    disciplinMatcher,
    estimerBukseStoerrelse,
    VARIANT_GRUPPER,
  };
})(typeof window !== 'undefined' ? window : globalThis);
